// "Dải hiệu lực" — điểm nhận diện duy nhất của SCR-050 (handoff docs/07-uiux §2).
// Thuần presentation của valid-time: mỗi version là một đoạn [validFrom … validTo/∞) trên trục
// thời gian; đoạn hiện hành (khớp currentVersion) tô màu action, đoạn quá khứ trung tính.
// Không dùng ngày hôm nay làm mốc — dải chỉ vẽ theo biên của các version đã có.
// Accessible name diễn đạt bằng ngôn ngữ nghiệp vụ (số giai đoạn, đoạn hiện hành), không đọc raw "vN";
// tooltip giữ nhãn có version vì chỉ xuất hiện khi người dùng chủ động trỏ vào từng đoạn.

import { Tooltip, Typography, theme } from "antd";
import { useMemo } from "react";
import { formatDateVn } from "../../../lib/dateFormat";
import type { AdminVersion } from "../adminTypes";

const DAY_MS = 86_400_000;
/** Đoạn đang mở (validTo = null) được vẽ dài tối thiểu một khoảng danh ước để không biến mất. */
const OPEN_SEGMENT_DAYS = 30;

function parseDate(value: string): number {
  return Date.parse(`${value}T00:00:00Z`);
}

type Segment = Readonly<{
  key: number;
  days: number;
  isCurrent: boolean;
  label: string;
}>;

export function ValidityBand({
  versions,
  currentVersion,
}: {
  readonly versions: readonly AdminVersion[];
  readonly currentVersion: number;
}) {
  const { token } = theme.useToken();

  const { segments, startLabel, endLabel } = useMemo(() => {
    const sorted = [...versions].sort((a, b) => a.validFrom.localeCompare(b.validFrom));
    if (sorted.length === 0) {
      return { segments: [] as Segment[], startLabel: "", endLabel: "" };
    }
    const last = sorted[sorted.length - 1] as AdminVersion;
    const nominalEnd =
      last.validTo == null ? parseDate(last.validFrom) + OPEN_SEGMENT_DAYS * DAY_MS : parseDate(last.validTo);
    const built: Segment[] = sorted.map((version) => {
      const start = parseDate(version.validFrom);
      const end = version.validTo == null ? nominalEnd : parseDate(version.validTo);
      return {
        key: version.version,
        days: Math.max(1, Math.round((end - start) / DAY_MS)),
        isCurrent: version.version === currentVersion,
        // Nhãn hiển thị theo định dạng ngày dùng chung; `validFrom` thô vẫn là khoá sắp xếp
        // và là mốc tính độ dài đoạn ở trên — chỉ đổi phần chữ.
        label: `v${version.version} · ${version.name} · ${version.status} · ${formatDateVn(version.validFrom)} → ${formatDateVn(version.validTo ?? "∞")}`,
      };
    });
    return {
      segments: built,
      startLabel: formatDateVn((sorted[0] as AdminVersion).validFrom),
      endLabel: formatDateVn(last.validTo ?? "∞"),
    };
  }, [versions, currentVersion]);

  if (segments.length === 0) {
    return null;
  }

  const ariaLabel = `Dải hiệu lực từ ${startLabel} đến ${endLabel}, gồm ${segments.length} giai đoạn thay đổi, giai đoạn hiện hành được đánh dấu`;

  return (
    <div className="admin-validity-band">
      <div role="img" aria-label={ariaLabel} style={{ display: "flex", gap: 2, height: 10 }}>
        {segments.map((segment) => (
          <Tooltip key={segment.key} title={segment.label}>
            <span
              style={{
                flexGrow: segment.days,
                flexBasis: 0,
                minWidth: 6,
                height: "100%",
                background: segment.isCurrent ? token.colorPrimary : token.colorFillSecondary,
                borderRadius: token.borderRadiusSM,
              }}
            />
          </Tooltip>
        ))}
      </div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {startLabel} → {endLabel}
      </Typography.Text>
    </div>
  );
}
